import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { Badge, LeaveTypeBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { format, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import { Inbox, CheckCircle2, XCircle, AlertCircle, Zap, ArrowRight } from 'lucide-react';

export default function ApprovalInboxPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Rejection modal state
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['approvals', 'inbox', activeTab],
    queryFn: async () => {
      const res = await approvalApi.getInbox({ status: activeTab });
      return res.data.data;
    },
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, action, comment }: { id: string, action: 'APPROVE' | 'REJECT' | 'ESCALATE', comment?: string }) => 
      approvalApi.action(id, { action, comment }),
    onSuccess: (_, variables) => {
      toast.success(`Leave ${variables.action.toLowerCase()}d successfully`);
      queryClient.invalidateQueries({ queryKey: ['approvals', 'inbox'] });
      setRejectingId(null);
      setRejectReason('');
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(variables.id);
        return newSet;
      });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  });

  const filteredApprovals = data?.filter((a: any) => a.status === activeTab) || [];

  const handleAction = (id: string, action: 'APPROVE' | 'ESCALATE') => {
    actionMutation.mutate({ id, action });
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    actionMutation.mutate({ id: rejectingId!, action: 'REJECT', comment: rejectReason });
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(filteredApprovals.map((a: any) => a.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleBulkApprove = async () => {
    if (!confirm(`Approve ${selectedIds.size} applications?`)) return;
    
    // Process sequentially to handle errors
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        await approvalApi.action(id, { action: 'APPROVE' });
        successCount++;
      } catch (e) {
        console.error(e);
      }
    }
    
    toast.success(`Successfully approved ${successCount} applications`);
    queryClient.invalidateQueries({ queryKey: ['approvals', 'inbox'] });
    setSelectedIds(new Set());
  };

  return (
    <div className="space-y-6 pb-24 relative">

      <div className="bg-white p-1 rounded-xl border border-slate-200 inline-flex">
        {['PENDING', 'APPROVED', 'REJECTED'].map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab as any); setSelectedIds(new Set()); }}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-slate-100 text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.charAt(0) + tab.slice(1).toLowerCase()}
            {tab === 'PENDING' && data?.filter((a: any) => a.status === 'PENDING').length > 0 && (
              <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                {data.filter((a: any) => a.status === 'PENDING').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-40 bg-slate-100 rounded-xl animate-pulse"></div>)}
        </div>
      ) : filteredApprovals.length === 0 ? (
        <div className="card py-16 flex flex-col items-center text-center animate-fade-in">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
            <Inbox className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700">Inbox Zero!</h3>
          <p className="text-slate-500 max-w-sm mt-1">You have no {activeTab.toLowerCase()} leave applications right now.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === 'PENDING' && (
            <div className="flex items-center gap-3 px-2">
              <input 
                type="checkbox" 
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                checked={selectedIds.size > 0 && selectedIds.size === filteredApprovals.length}
                onChange={handleSelectAll}
              />
              <span className="text-sm font-medium text-slate-600">Select All</span>
            </div>
          )}

          {filteredApprovals.map((item: any) => {
            const leave = item.application;
            const applicant = leave.user;
            const daysToStart = differenceInDays(new Date(leave.fromDate), new Date());
            const isUrgent = activeTab === 'PENDING' && daysToStart <= 3;
            
            return (
              <div key={item.id} className={`card p-5 animate-fade-in transition-all ${selectedIds.has(item.id) ? 'ring-2 ring-blue-500 bg-blue-50/20' : 'hover:shadow-md'}`}>
                <div className="flex flex-col lg:flex-row gap-6">
                  
                  {/* Left: Applicant info & Select */}
                  <div className="flex items-start gap-4 lg:w-1/4 shrink-0">
                    {activeTab === 'PENDING' && (
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 mt-2 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={selectedIds.has(item.id)}
                        onChange={() => handleSelect(item.id)}
                      />
                    )}
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0 border border-slate-200">
                      {applicant.photoUrl ? (
                        <img src={applicant.photoUrl} alt={applicant.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
                      ) : (
                        <span className="text-lg font-bold text-slate-500">{applicant.name.charAt(0)}</span>
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">{applicant.name}</h4>
                      <p className="text-xs text-slate-500">{applicant.designation}</p>
                      <p className="text-xs text-slate-400 mt-1">{applicant.department}</p>
                    </div>
                  </div>

                  {/* Middle: Leave Details */}
                  <div className="flex-1 lg:border-l lg:border-slate-100 lg:pl-6">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <LeaveTypeBadge colour={leave.leaveType.colour} name={leave.leaveType.name} />
                      <span className="text-sm font-semibold text-slate-800">
                        {format(new Date(leave.fromDate), 'MMM d, yyyy')} - {format(new Date(leave.toDate), 'MMM d, yyyy')}
                      </span>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                        {leave.duration} days
                      </span>
                      {isUrgent && (
                        <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full animate-pulse-soft border border-red-100">
                          <AlertCircle className="w-3 h-3" /> Starts in {daysToStart} days
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 line-clamp-2 bg-slate-50 p-3 rounded-lg border border-slate-100">
                      <span className="font-medium text-slate-700">Reason:</span> {leave.reason}
                    </p>

                    {item.status === 'PENDING' && leave.approvalChain && (
                      <div className="mt-4 flex items-center gap-2 text-xs text-slate-500 overflow-x-auto scrollbar-hide pb-1">
                        <span className="font-medium">Path:</span>
                        {leave.approvalChain.map((step: any, idx: number) => (
                          <React.Fragment key={step.id}>
                            <span className={`px-2 py-1 rounded ${step.id === item.id ? 'bg-blue-100 text-blue-800 font-bold border border-blue-200' : step.status === 'APPROVED' ? 'bg-green-50 text-green-700' : 'bg-slate-50'}`}>
                              {step.levelName}
                            </span>
                            {idx < leave.approvalChain.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-row lg:flex-col items-center justify-end gap-2 lg:w-40 shrink-0 lg:border-l lg:border-slate-100 lg:pl-6 pt-4 lg:pt-0 border-t border-slate-100 lg:border-t-0">
                    {item.status === 'PENDING' ? (
                      <>
                        <Button 
                          variant="success" fullWidth onClick={() => handleAction(item.id, 'APPROVE')}
                          disabled={actionMutation.isPending}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
                        </Button>
                        <Button 
                          variant="danger" fullWidth onClick={() => setRejectingId(item.id)}
                          disabled={actionMutation.isPending}
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Reject
                        </Button>
                        {/* Only show escalate if it's not the final step (MD) */}
                        {user?.role !== 'MD' && (
                          <Button 
                            variant="ghost" fullWidth onClick={() => handleAction(item.id, 'ESCALATE')}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          >
                            <Zap className="w-4 h-4 mr-2" /> Escalate
                          </Button>
                        )}
                      </>
                    ) : (
                      <div className="text-center w-full">
                        <Badge status={item.status} className="mb-2" />
                        <p className="text-xs text-slate-500">
                          {item.updatedAt ? format(new Date(item.updatedAt), 'MMM d, yyyy h:mm a') : item.actionedAt ? format(new Date(item.actionedAt), 'MMM d, yyyy h:mm a') : '—'}
                        </p>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && activeTab === 'PENDING' && (
        <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-white border-t border-slate-200 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] p-4 z-40 animate-slide-up">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold">
                {selectedIds.size}
              </span>
              <span className="font-medium text-slate-700">requests selected</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setSelectedIds(new Set())}>Cancel</Button>
              <Button variant="success" size="lg" onClick={handleBulkApprove}>
                Approve All Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
            <div className="bg-red-50 p-6 text-center border-b border-red-100">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-red-900">Reject Leave Application</h2>
            </div>
            <div className="p-6">
              <Textarea
                label="Reason for Rejection (Required)"
                placeholder="Please provide a constructive reason..."
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                autoFocus
              />
              <div className="flex items-center justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setRejectingId(null)}>Cancel</Button>
                <Button variant="danger" onClick={handleRejectSubmit} disabled={!rejectReason.trim() || actionMutation.isPending}>
                  Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
